import { useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { ReactComponent as AddIcon } from 'images/icons/add.svg';
import { ReactComponent as CloseIcon } from 'images/icons/close.svg';
import { QUOTE_SECTION } from 'shared/constants/common';
import { DEFAULT_SECONDARY_COLOUR } from 'shared/constants/styleConstants';

const StyledNavMenu = styled.div`
  background-color: white;
  display: flex;
  font-size: 18px;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;

    ${(props) =>
      props.menuOpen &&
      `
          height: 100vh;
          width: 100vw;
          position: fixed;
          overflow: hidden;
          top: 0;
          left: 0;
        `}
  }

  @media (min-width: 769px) {
    max-width: 95%;
  }

  @media (min-width: 1280px) {
    max-width: 1440px;
  }
`;

function NavLink({
  label,
  value,
  isMenuOpen,
  openMenu,
  closeMenu,
  isActiveLink,
}) {
  const { theme } = useSelector((state) => state.ui);

  const StyledNavLink = styled.div`
    flex-grow: 1;
    font-weight: 700;
    text-align: center;
    padding-top: 1rem;
    display: flex;
    flex-direction: column;
    font-size: 16px;

    a {
      height: 100%;
      width: 100%;
    }

    .highlightBar {
      width: 100%;
      background-color: ${(props) =>
        props.activeLinkHighlight &&
        (theme.brandColour || DEFAULT_SECONDARY_COLOUR)};
      border-radius: 99px;
      padding: 2px;
      margin-top: 15px;
    }

    &:hover {
      cursor: pointer;
    }

    .toggleMenuIcon {
      display: none;
    }

    @media (hover: hover) {
      &:hover > a {
        color: ${theme.brandColour || DEFAULT_SECONDARY_COLOUR};
      }
    }

    @media (max-width: 768px) {
      display: ${(props) => (props.displayLink ? 'flex' : 'none')};
      flex-direction: row;
      border-bottom: 1px solid #f0f0f0;
      text-align: left;
      align-items: center;
      justify-content: center;
      padding: 29px 0 29px 19px;
      height: 77px;
      max-height: 77px;

      .highlightBar {
        display: none;
      }

      .toggleMenuIcon {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 44px;
        width: 44px;
      }
    }
  `;

  return (
    <StyledNavLink
      displayLink={isActiveLink ? true : isMenuOpen}
      activeLinkHighlight={isActiveLink}
    >
      <a href={`#${value}`} onClick={closeMenu}>
        {label}
      </a>
      {isMenuOpen && value === QUOTE_SECTION.INTRODUCTION && (
        <button type="button" className="toggleMenuIcon" onClick={closeMenu}>
          <CloseIcon />
        </button>
      )}
      {!isMenuOpen && isActiveLink && (
        <button type="button" className="toggleMenuIcon" onClick={openMenu}>
          <AddIcon />
        </button>
      )}
      <div className="highlightBar" />
    </StyledNavLink>
  );
}

export default function NavigationBar({
  activeLink = QUOTE_SECTION.INTRODUCTION,
}) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navLinks = [
    { value: QUOTE_SECTION.INTRODUCTION, label: 'Introduction' },
    { value: QUOTE_SECTION.OVERVIEW, label: 'Overview' },
    { value: QUOTE_SECTION.SUMMARY, label: 'Summary' },
    { value: QUOTE_SECTION.PRICE, label: 'Price' },
  ];

  const openMobileMenu = () => {
    setIsMobileMenuOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    document.body.style.overflow = 'unset';
  };

  return (
    <div className="bg-white sticky top-0 left-0 z-[100] flex justify-center">
      <StyledNavMenu menuOpen={isMobileMenuOpen}>
        {navLinks.map(({ value, label }) => {
          return (
            <NavLink
              isActiveLink={value === activeLink}
              key={label}
              label={label}
              value={value}
              isMenuOpen={isMobileMenuOpen}
              openMenu={openMobileMenu}
              closeMenu={closeMobileMenu}
            />
          );
        })}
      </StyledNavMenu>
    </div>
  );
}
